import { HourBuffCategories } from 'custom.d';
import * as dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import moment from 'moment-timezone';

const padTo2Digits = (num: number) => num.toString().padStart(2, '0');

export const formatDate = (stringTimeStamp: string) => {
  const date = new Date(parseInt(stringTimeStamp, 10) * 1000);

  return [padTo2Digits(date.getDate()), padTo2Digits(date.getMonth() + 1), date.getFullYear()].join(
    '/',
  );
};

export const newFormatDate = (stringTimeStamp: string) => {
  const date = stringTimeStamp.slice(0, 10);
  const temp = date.split('-');
  const to = `${temp[2]}.${temp[1]}.${temp[0]}`;

  return to;
};

export const calculateDate = (date: string) => {
  dayjs.extend(relativeTime);

  return dayjs(date).fromNow();
};

export function formatDateStringSplitDots(inputDateString: string, locale = 'en-GB'): string {
  const date = new Date(inputDateString);

  const day = date.toLocaleDateString(locale, { day: '2-digit' });
  const month = date.toLocaleDateString(locale, { month: '2-digit' });
  const year = date.toLocaleDateString(locale, { year: 'numeric' });

  const formattedDate = `${day}.${month}.${year}`;

  return formattedDate;
}

export const formatDateToDateMonthYear = (dateString: string) => {
  const options = { day: '2-digit', month: 'short', year: 'numeric' };
  const date = new Date(dateString);

  return date.toLocaleDateString('en-GB', options as Intl.DateTimeFormatOptions);
};

export const formatDateToDateFullMonthYear = (dateString: string) => {
  const options = { day: '2-digit', month: 'long', year: 'numeric' };
  const date = new Date(dateString);

  return date.toLocaleDateString('en-GB', options as Intl.DateTimeFormatOptions);
};

export const formatTimeToGMT = (dateString: string) => {
  const gmtTime = moment.tz(dateString, 'GMT');

  const localTime = gmtTime.clone().tz(Intl.DateTimeFormat().resolvedOptions().timeZone);

  const formattedTime = localTime.format('HH:mm');

  const offset = localTime.format('Z');

  return `${formattedTime} (${offset} GMT)`;
};

export const getHourBufferCategory = (offsetDiff: number) => {
  const hoursDiff = Math.abs(offsetDiff / 60);

  const categoryEntry = Object.entries(HourBuffCategories).find(([key, range]) => {
    const [min, max] = range.split('-').map(Number);

    return hoursDiff >= min && hoursDiff < max;
  });

  return categoryEntry ? parseInt(categoryEntry[0], 10) : 9;
};

/**
 * Converts an ISO date string to a custom date format DD.MM.YYYY
 * @param isoDateString - ISO date string (e.g., "2024-12-30T05:35:12.183Z")
 * @returns formatted date string in DD.MM.YYYY format
 */
export const formatDateToDayMonthYear = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date string');
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-based
  const year = date.getFullYear();

  return `${day}.${month}.${year}`;
};

export const generateDateTime = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hours = String(now.getHours()).padStart(2, '0');
  const minutes = String(now.getMinutes()).padStart(2, '0');
  const seconds = String(now.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day}_${hours}:${minutes}:${seconds}`;
};

export const getCurrentMonthDates = () => {
  const currentDate = new Date();
  const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1); // First day of the current month
  const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0); // Last day of the current month

  return {
    startDate: startDate.toISOString().split('T')[0], // Format to yyyy-mm-dd
    endDate: endDate.toISOString().split('T')[0], // Format to yyyy-mm-dd
  };
};

export const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Jun 5, 2025
export function formatDateToShortMonth(input: string): string {
  let date: Date;

  const ddMmYyyyRegex = /^\d{2}\/\d{2}\/\d{4}$/;

  if (ddMmYyyyRegex.test(input)) {
    // Format: DD/MM/YYYY
    const [dayStr, monthStr, yearStr] = input.split('/');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-based
    const year = parseInt(yearStr, 10);

    date = new Date(year, month, day);
  } else {
    // Assume ISO or parseable format
    const parsed = Date.parse(input);
    if (Number.isNaN(parsed)) {
      throw new Error('Invalid date format');
    }

    date = new Date(parsed);
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const formatDateToDateFullMonthYearWithTodayAndYesterday = (dateString: string) => {
  const options = { day: '2-digit', month: 'long', year: 'numeric' } as const;
  const inputDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const isSameDate = (d1: Date, d2: Date) =>
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();

  if (isSameDate(inputDate, today)) {
    return 'Today';
  }

  if (isSameDate(inputDate, yesterday)) {
    return 'Yesterday';
  }

  return inputDate.toLocaleDateString('en-GB', options);
};

// 5 June 2025
export function formatDateToDayShortMonthYear(input: string): string {
  let date: Date;

  const ddMmYyyyRegex = /^\d{2}\/\d{2}\/\d{4}$/;

  if (ddMmYyyyRegex.test(input)) {
    // Format: DD/MM/YYYY
    const [dayStr, monthStr, yearStr] = input.split('/');
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10) - 1; // JavaScript months are 0-based
    const year = parseInt(yearStr, 10);

    date = new Date(year, month, day);
  } else {
    // Assume ISO or parseable format
    const parsed = Date.parse(input);
    if (Number.isNaN(parsed)) {
      throw new Error('Invalid date format');
    }

    date = new Date(parsed);
  }

  const day = date.getDate();
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}
